import {Injectable} from '@angular/core';
import {BaseService} from '../utils/base.service';
import {catchError, map} from 'rxjs/operators';
import {RequestParamModel} from '../models/request-params.model';
import {PaginationModel} from '../models';
import { Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(private baseService: BaseService) {
  }

  markNotificationAsRead(requestModel: any, notificationId: any) {
    if (notificationId) {
      return this.baseService.patch('notification/' + notificationId, requestModel)
        .pipe(map<any, number>(response => {
            requestModel = response as any;
            return requestModel;
          }),
          catchError(err => throwError(() => err))
      );
    }
  }

  /** @param skipLoader the caller shows its own loading state (the notification
   *  feed's veil and "Load more"), so the app-wide spinner stays down. */
  getUserNotifications(requestParamModel: RequestParamModel, skipLoader: boolean = false): any {
    return this.baseService.get('notification', requestParamModel, skipLoader)
      .pipe(map<any, any>(response => {
        const responseList = new PaginationModel<any>();
        const headers = response.headers;
        responseList.setHeaders(headers);
        responseList.data = response.body.data as any[];
        return responseList;
        }),
      catchError(err => throwError(() => err))
  );
  }

  /** The signed-in user's unread notifications counted per sender — the numbers
   *  in the notification panel's sender menu. Served by api-v2 (see base.service's
   *  Node path list); never drives the app-wide loader. */
  getSenderUnreadCounts(): Observable<{ from_user_id: number; unread: number }[]> {
    return this.baseService.get('notification/sender-unread-counts', undefined, true)
      .pipe(map((response: any) => response.body?.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * One page of the "View all notifications" page — api-v2 `GET notification/feed`
   * (see base.service's Node path list). `params` are the page's filters plus
   * `limit` / `before_id`; empty values are left out. Never drives the app-wide
   * loader: the page shows its own.
   */
  getNotificationFeed(params: Record<string, any>): Observable<{ total: number; remaining: number; rows: any[] }> {
    return this.baseService.get('notification/feed' + NotificationService.query(params), undefined, true)
      .pipe(map((response: any) => response.body?.data ?? {total: 0, remaining: 0, rows: []}),
        catchError(err => throwError(() => err))
      );
  }

  /** The same page's counts — api-v2 `GET notification/feed-counts`, same filters. */
  getNotificationFeedCounts(params: Record<string, any>): Observable<any> {
    return this.baseService.get('notification/feed-counts' + NotificationService.query(params), undefined, true)
      .pipe(map((response: any) => response.body?.data ?? null),
        catchError(err => throwError(() => err))
      );
  }

  /** The account / contact names behind some of the caller's notifications —
   *  api-v2 `GET notification/record-names`, for the panel's chips. */
  getNotificationRecordNames(ids: number[]): Observable<{ notification_id: number; company: any; frontendUser: any }[]> {
    return this.baseService.get('notification/record-names' + NotificationService.query({ids: ids.join(',')}), undefined, true)
      .pipe(map((response: any) => response.body?.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  private static query(params: Record<string, any>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        search.set(key, String(value));
      }
    });
    const text = search.toString();
    return text ? '?' + text : '';
  }

  markAllNotificationsAsRead(requestData: any) {
    return this.baseService.post('notification/mark-all-as-read', requestData)
      .pipe(map<any, any>(response => {
        return response as any;
      }),
      catchError(err => throwError(() => err))
  );
  }

  sendNotification(requestModel: any) {
    // Best-effort side-effect of saving a note with @mentions: skip the global
    // loader AND the global error handler (skipError=true). A failure here (e.g.
    // a 403 CSRF when the console runs on a different origin than the API) must
    // NOT trip the interceptor's 403 redirect and bounce the user off the page —
    // the note itself is already saved. Callers subscribe with an error handler.
    return this.baseService.post(`notification`, requestModel, true, true).pipe(
      map<any, any>(response => {
        return response.data;
      }),
      catchError(err => throwError(() => err))
  );
  }


  deleteNotification(_id: any) {
    return this.baseService.delete('notification/' + _id)
      .pipe(map<any, number>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
    );
  }

  deleteNotificationByNoteAndUser(noteId: number, toUserId: number) {
    return this.baseService.delete(`notification?note_id=${noteId}&to_user_id=${toUserId}`)
      .pipe(map<any, any>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
    );
  }

  /** @param skipError caller reports the failure itself (see toggleLike) — stops
   *  ErrorInterceptor raising a second, generic toast for the same failure. */
  updateNoteReactions(requestData: any, skipError: boolean = false) {
    return this.baseService.post('note-reaction', requestData, false, skipError)
      .pipe(map<any, any>(response => {
        return response as any;
     }),
      catchError(err => throwError(() => err))
  );
  }

  /** @param skipError see updateNoteReactions. */
  deleteNoteReactions(_id: any, skipError: boolean = false) {
    return this.baseService.delete('note-reaction/' + _id, false, skipError)
      .pipe(map<any, number>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
    );
  }

}
